import React from "react";
import {
  Stack,
  SimpleGrid,
  TextInput,
  Textarea,
  NumberInput,
  Select,
  Switch,
  Text,
  Box,
} from "@mantine/core";
import { DatePicker, TimeInput } from "@mantine/dates";
import moment from "moment";
import RemoteSelect2 from "../remoteSelect2";
import {
  REF_ENTITY_CONFIG,
  isRefExtraFieldType,
  isFileExtraFieldType,
  isMultiFileExtraFieldType,
  getExtraFieldNumberPrecision,
} from "../../data/extraFieldTypes";
import ExtraFieldFileInput from "./ExtraFieldFileInput";
import FormReadOnlyField from "../formReadOnlyField";
import {
  formatFormFieldDisplay,
  getExtraFieldDisplayKind,
} from "../../helpers/formatFormFieldDisplay";
import { DATE_INPUT_FORMAT } from "../../helpers/dateInputFormat";
import { normalizeExtraFieldValuesFromApi } from "../../helpers/extraFieldValues";

const parseDateValue = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const isFullWidthExtraField = (fieldType) =>
  fieldType === "TEXTAREA" ||
  fieldType === "DATETIME" ||
  fieldType === "FILES";

const ExtraFieldsInput = ({
  definitions = [],
  values = {},
  onChange,
  form,
  columns = 1,
  readOnly = false,
  disabled = false,
}) => {
  if (!definitions.length) return null;

  const updateValue = (key, nextValue) => {
    const current = normalizeExtraFieldValuesFromApi(
      form?.values?.extraFieldValues ?? values,
    );
    onChange?.({
      ...current,
      [key]: nextValue,
    });
  };

  const renderField = (def) => {
    const label = def.cname ? `${def.label} / ${def.cname}` : def.label;
    const value = values?.[def.key];
    const commonLabel = label;
    const locked = disabled || readOnly;

    if (locked && !isFileExtraFieldType(def.fieldType)) {
      const selectData = (def.selectOptions || []).map((o) => ({
        value: o.label,
        label: o.cname ? `${o.label} / ${o.cname}` : o.label,
      }));
      return (
        <FormReadOnlyField
          key={def.key}
          label={commonLabel}
          preLine={def.fieldType === "TEXTAREA"}
          value={formatFormFieldDisplay(value, {
            kind: getExtraFieldDisplayKind(def.fieldType),
            props: {
              precision: getExtraFieldNumberPrecision(def.fieldType),
              data: selectData,
              labelField: REF_ENTITY_CONFIG[def.fieldType]?.labelField,
              labelRender: REF_ENTITY_CONFIG[def.fieldType]?.labelRender,
            },
          })}
        />
      );
    }

    if (def.fieldType === "TEXTAREA") {
      return (
        <Textarea
          key={def.key}
          label={commonLabel}
          value={value ?? ""}
          disabled={disabled || readOnly}
          minRows={2}
          onChange={(e) => updateValue(def.key, e.target.value)}
        />
      );
    }

    if (def.fieldType === "INTEGER" || def.fieldType === "DECIMAL_2" || def.fieldType === "DECIMAL_4") {
      return (
        <NumberInput
          key={def.key}
          label={commonLabel}
          value={value ?? ""}
          precision={getExtraFieldNumberPrecision(def.fieldType)}
          disabled={disabled || readOnly}
          onChange={(v) => updateValue(def.key, v)}
        />
      );
    }

    if (def.fieldType === "DATE") {
      return (
        <DatePicker
          key={def.key}
          label={commonLabel}
          value={parseDateValue(value)}
          disabled={disabled || readOnly}
          allowFreeInput
          inputFormat={DATE_INPUT_FORMAT}
          onChange={(v) => updateValue(def.key, v)}
        />
      );
    }

    if (def.fieldType === "DATETIME") {
      const dateValue = parseDateValue(value);
      return (
        <Stack key={def.key} spacing="xs">
          <Text size="sm" weight={500}>
            {commonLabel}
          </Text>
          <DatePicker
            value={dateValue}
            disabled={disabled || readOnly}
            allowFreeInput
            inputFormat={DATE_INPUT_FORMAT}
            onChange={(v) => {
              if (!v) return updateValue(def.key, null);
              const base = dateValue || new Date();
              const merged = moment(v)
                .hour(moment(base).hour())
                .minute(moment(base).minute())
                .toDate();
              updateValue(def.key, merged);
            }}
          />
          <TimeInput
            value={dateValue}
            disabled={disabled || readOnly}
            onChange={(v) => {
              if (!v) return;
              const base = dateValue || new Date();
              const merged = moment(base)
                .hour(moment(v).hour())
                .minute(moment(v).minute())
                .toDate();
              updateValue(def.key, merged);
            }}
          />
        </Stack>
      );
    }

    if (def.fieldType === "SELECT") {
      return (
        <Select
          key={def.key}
          label={commonLabel}
          value={value ?? null}
          disabled={disabled || readOnly}
          data={(def.selectOptions || []).map((o) => ({
            value: o.label,
            label: o.cname ? `${o.label} / ${o.cname}` : o.label,
          }))}
          searchable
          clearable
          onChange={(v) => updateValue(def.key, v)}
        />
      );
    }

    if (def.fieldType === "BOOLEAN") {
      return (
        <Switch
          key={def.key}
          label={commonLabel}
          checked={!!value}
          disabled={disabled || readOnly}
          onChange={(e) => updateValue(def.key, e.currentTarget.checked)}
        />
      );
    }

    if (isRefExtraFieldType(def.fieldType)) {
      const config = REF_ENTITY_CONFIG[def.fieldType];
      return (
        <RemoteSelect2
          key={def.key}
          label={commonLabel}
          apiEntity={config.apiEntity}
          searchFields={config.searchFields}
          labelRender={config.labelRender}
          pageSize={config.pageSize}
          valueField="_id"
          clearable
          disabled={disabled || readOnly}
          value={value}
          onChange={(v) => updateValue(def.key, v)}
        />
      );
    }

    if (isFileExtraFieldType(def.fieldType)) {
      return (
        <ExtraFieldFileInput
          key={def.key}
          form={form}
          fieldKey={def.key}
          label={commonLabel}
          multiple={isMultiFileExtraFieldType(def.fieldType)}
          readOnly={locked}
          disabled={locked}
        />
      );
    }

    return (
      <TextInput
        key={def.key}
        label={commonLabel}
        value={value ?? ""}
        disabled={disabled || readOnly}
        onChange={(e) => updateValue(def.key, e.target.value)}
      />
    );
  };

  const fieldNodes = definitions.map((def) => {
    const field = renderField(def);
    if (columns !== 2 || !isFullWidthExtraField(def.fieldType)) {
      return field;
    }
    return (
      <Box key={def.key} sx={{ gridColumn: "1 / -1" }}>
        {field}
      </Box>
    );
  });

  if (columns === 2) {
    return (
      <SimpleGrid
        cols={2}
        spacing="md"
        breakpoints={[{ maxWidth: "sm", cols: 1 }]}
      >
        {fieldNodes}
      </SimpleGrid>
    );
  }

  return <Stack spacing="md">{fieldNodes}</Stack>;
};

export default ExtraFieldsInput;
